/**
 * Weighted A* over a NavGrid. 8-directional with no corner-cutting (a diagonal
 * step is only allowed when both shared orthogonal tiles are passable). The
 * heuristic is octile distance scaled by the minimum possible per-step cost, so
 * it stays admissible even where roads discount movement below 1 — A* therefore
 * returns a genuinely optimal path, deterministically (see BinaryHeap tie-break).
 */
import { ROAD_COST_FACTOR } from '../roads/roadNetwork.js';
import type { NavGrid } from './navGrid.js';
import { BinaryHeap } from './binaryHeap.js';

const SQRT2 = Math.SQRT2;
/** Cheapest a single unit of geometric distance can cost (grass on a road). */
const MIN_STEP_COST = ROAD_COST_FACTOR;

interface Dir {
  readonly dx: number;
  readonly dy: number;
  readonly diagonal: boolean;
}

// Fixed neighbour order (orthogonal first) — determinism comes from the heap
// tie-break, but a stable order keeps behaviour easy to reason about.
const DIRS: readonly Dir[] = [
  { dx: 1, dy: 0, diagonal: false },
  { dx: -1, dy: 0, diagonal: false },
  { dx: 0, dy: 1, diagonal: false },
  { dx: 0, dy: -1, diagonal: false },
  { dx: 1, dy: 1, diagonal: true },
  { dx: 1, dy: -1, diagonal: true },
  { dx: -1, dy: 1, diagonal: true },
  { dx: -1, dy: -1, diagonal: true },
];

export interface PathResult {
  /** Tile indices from start to goal, inclusive. */
  readonly path: number[];
  /** Total traversal cost. */
  readonly cost: number;
  /** Nodes expanded (for profiling / tests). */
  readonly expanded: number;
}

const octile = (dx: number, dy: number): number => {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  return ax > ay ? ax - ay + SQRT2 * ay : ay - ax + SQRT2 * ax;
};

export const findPath = (
  nav: NavGrid,
  startX: number,
  startY: number,
  goalX: number,
  goalY: number,
): PathResult | null => {
  if (!nav.passable(startX, startY) || !nav.passable(goalX, goalY)) return null;

  const start = nav.index(startX, startY);
  const goal = nav.index(goalX, goalY);
  if (start === goal) return { path: [start], cost: 0, expanded: 0 };

  const open = new BinaryHeap();
  const gScore = new Map<number, number>();
  const cameFrom = new Map<number, number>();
  const closed = new Set<number>();

  gScore.set(start, 0);
  open.push({ node: start, f: octile(goalX - startX, goalY - startY) * MIN_STEP_COST, tie: start });

  let expanded = 0;
  while (open.size > 0) {
    const current = open.pop();
    if (!current) break;
    const node = current.node;
    if (closed.has(node)) continue;
    closed.add(node);
    expanded++;

    if (node === goal) {
      return { path: reconstruct(cameFrom, node), cost: gScore.get(goal) ?? 0, expanded };
    }

    const cx = node % nav.width;
    const cy = (node - cx) / nav.width;
    const g = gScore.get(node) ?? Number.POSITIVE_INFINITY;

    for (const dir of DIRS) {
      const nx = cx + dir.dx;
      const ny = cy + dir.dy;
      if (!nav.passable(nx, ny)) continue;
      if (dir.diagonal) {
        // No corner-cutting: both orthogonal neighbours must be passable.
        if (!nav.passable(cx + dir.dx, cy) || !nav.passable(cx, cy + dir.dy)) continue;
      }
      const ni = nav.index(nx, ny);
      if (closed.has(ni)) continue;

      const step = nav.cost(nx, ny) * (dir.diagonal ? SQRT2 : 1);
      const tentative = g + step;
      if (tentative < (gScore.get(ni) ?? Number.POSITIVE_INFINITY)) {
        cameFrom.set(ni, node);
        gScore.set(ni, tentative);
        const h = octile(goalX - nx, goalY - ny) * MIN_STEP_COST;
        open.push({ node: ni, f: tentative + h, tie: ni });
      }
    }
  }

  return null;
};

const reconstruct = (cameFrom: Map<number, number>, goal: number): number[] => {
  const path = [goal];
  let node = goal;
  for (;;) {
    const prev = cameFrom.get(node);
    if (prev === undefined) break;
    path.push(prev);
    node = prev;
  }
  path.reverse();
  return path;
};
